/**
 * Document Service
 * Handles all document processing API calls
 */

import { AxiosError, AxiosProgressEvent } from 'axios';
import apiClient, { uploadClient } from 'services/apiClient';
import { API_ENDPOINTS, API_CONFIG } from 'config/api.config';
import useAuthStore from 'stores/authStore';
import {
  FileValidationResult,
  UploadProgressCallback,
  JobStatusCallback,
  UploadResponse,
  JobStatusResponse,
  ProcessingResult,
  JobCancellationResponse,
  DocumentServiceError,
  UserDocumentHistoryResponse,
  ClassificationResultsResponse,
} from 'types/document.types';

/**
 * Validate file before upload
 * @param file - File to validate
 * @returns Validation result with errors if any
 */
export const validateFile = (file: File): FileValidationResult => {
  const errors: string[] = [];

  // Check file size
  if (file.size > API_CONFIG.FILE_UPLOAD.MAX_SIZE) {
    errors.push(`File size exceeds ${API_CONFIG.FILE_UPLOAD.MAX_SIZE / (1024 * 1024)}MB limit`);
  }

  // Check file type
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
  const fileMimeType = file.type;

  const isValidExtension = API_CONFIG.FILE_UPLOAD.ACCEPTED_TYPES.includes(fileExtension);
  const isValidMimeType = API_CONFIG.FILE_UPLOAD.ACCEPTED_MIME_TYPES.includes(fileMimeType);

  if (!isValidExtension && !isValidMimeType) {
    errors.push(
      `Invalid file type. Accepted types: ${API_CONFIG.FILE_UPLOAD.ACCEPTED_TYPES.join(', ')}`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Document Service Class
 */
class DocumentService {
  /**
   * Upload document for async processing
   * @param file - File to upload
   * @param message - Optional message/prompt for processing
   * @param onUploadProgress - Optional progress callback
   * @returns Promise with upload response containing job_id
   */
  async uploadAsync(
    file: File,
    message?: string,
    onUploadProgress: UploadProgressCallback | null = null
  ): Promise<UploadResponse> {
    // Validate file
    const validation = validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const formData = new FormData();
    formData.append('file', file);

    // Add message if provided
    if (message && message.trim()) {
      formData.append('message', message.trim());
    }

    // Add user_id from auth store
    const userId = useAuthStore.getState().getUserId();
    if (userId) {
      formData.append('user_id', userId);
      console.log('[Upload] Adding user_id to upload:', userId);
    } else {
      console.warn('[Upload] No user_id found in auth store');
    }

    try {
      const response = await uploadClient.post<UploadResponse>(
        API_ENDPOINTS.DOCUMENTS.UPLOAD_ASYNC,
        formData,
        {
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onUploadProgress && progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onUploadProgress(percentCompleted);
            }
          },
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Get job status
   * @param jobId - Job identifier
   * @param timeout - Optional request timeout in milliseconds
   * @returns Promise with job status
   */
  async getStatus(jobId: string, timeout?: number): Promise<JobStatusResponse> {
    try {
      const response = await apiClient.get<JobStatusResponse>(
        API_ENDPOINTS.DOCUMENTS.STATUS(jobId),
        timeout ? { timeout } : undefined
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Get job result
   * @param jobId - Job identifier
   * @returns Promise with processing result
   */
  async getResult(jobId: string): Promise<ProcessingResult> {
    try {
      const response = await apiClient.get<ProcessingResult>(
        API_ENDPOINTS.DOCUMENTS.RESULT(jobId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Cancel a job
   * @param jobId - Job identifier
   * @returns Promise with cancellation confirmation
   */
  async cancelJob(jobId: string): Promise<JobCancellationResponse> {
    try {
      const response = await apiClient.delete<JobCancellationResponse>(
        API_ENDPOINTS.DOCUMENTS.CANCEL(jobId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Get user's document history
   * @param userId - User identifier
   * @param limit - Maximum number of documents to return (default: 50)
   * @param offset - Number of documents to skip (default: 0)
   * @returns Promise with user document history
   */
  async getUserHistory(
    userId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<UserDocumentHistoryResponse> {
    try {
      const response = await apiClient.get<UserDocumentHistoryResponse>(
        `${API_ENDPOINTS.DOCUMENTS.HISTORY(userId)}`,
        {
          params: {
            limit,
            offset
          }
        }
      );
      console.log('[DocumentService] User history fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('[DocumentService] Error fetching user history:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Get user's classification results (all completed classifications)
   * @param userId - User identifier
   * @param limit - Maximum number of results to return (default: 50)
   * @param offset - Number of results to skip (default: 0)
   * @returns Promise with classification results
   */
  async getClassificationResults(
    userId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ClassificationResultsResponse> {
    try {
      const response = await apiClient.get<ClassificationResultsResponse>(
        API_ENDPOINTS.DOCUMENTS.RESULTS(userId),
        { params: { limit, offset } }
      );
      return response.data;
    } catch (error) {
      console.error('[DocumentService] Error fetching classification results:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Poll job status until completion
   * @param jobId - Job identifier
   * @param onProgress - Optional progress callback
   * @returns Promise with final result
   */
  async pollJobStatus(
    jobId: string,
    onProgress: JobStatusCallback | null = null
  ): Promise<ProcessingResult> {
    let attempts = 0;
    const maxAttempts = API_CONFIG.POLLING.MAX_ATTEMPTS;
    const pollInterval = API_CONFIG.POLLING.INTERVAL;
    const requestTimeout = API_CONFIG.POLLING.REQUEST_TIMEOUT;
    const retryDelay = API_CONFIG.POLLING.RETRY_DELAY;

    return new Promise<ProcessingResult>((resolve, reject) => {
      const checkStatus = async (): Promise<void> => {
        try {
          attempts++;

          if (attempts > maxAttempts) {
            reject(new Error('Polling timeout: Maximum attempts reached'));
            return;
          }

          const statusData = await this.getStatus(jobId, requestTimeout);

          // Call progress callback
          if (onProgress) {
            onProgress(statusData);
          }

          // Check for verification requirement
          if (statusData.status === 'verification_in_progress') {
            // Stop polling, verification flow takes over
            // The UI will handle verification through the store
            return;
          }

          // Check job status
          if (statusData.status === 'completed') {
            // Return result from status response or fetch separately
            if (statusData.result) {
              resolve(statusData.result);
            } else {
              const result = await this.getResult(jobId);
              resolve(result);
            }
            return;
          }

          if (statusData.status === 'failed') {
            reject(new Error(statusData.error?.message || 'Job processing failed'));
            return;
          }

          if (statusData.status === 'cancelled') {
            reject(new Error('Job was cancelled'));
            return;
          }

          // Continue polling
          setTimeout(checkStatus, pollInterval);
        } catch (error: any) {
          // Check if error is recoverable (timeout or connection issue)
          const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
          const isConnectionError = error?.message?.includes('No response from server') ||
                                    error?.code === 'ECONNREFUSED' ||
                                    error?.code === 'ERR_NETWORK';

          if (isTimeout || isConnectionError) {
            // Backend might be initializing or temporarily unavailable - retry
            console.warn(`Poll request failed (${isTimeout ? 'timeout' : 'connection error'}), retrying in ${retryDelay}ms...`);
            setTimeout(checkStatus, retryDelay);
          } else {
            // Other errors should reject (e.g., 404, 500, etc.)
            reject(error);
          }
        }
      };

      // Start polling after initial delay to allow backend to initialize job status
      setTimeout(checkStatus, pollInterval);
    });
  }

  /**
   * Watch job progress via SSE stream.
   * Uses fetch + ReadableStream so we can attach the Authorization header
   * (native EventSource does not support custom headers).
   *
   * SSE event flow:
   *   status  → initial snapshot
   *   update  → progress updates; fires onPartialResult when HTS is done
   *   complete→ full result (hts_classification + tariff_determination)
   *   error   → unrecoverable failure
   *
   * Falls back to polling if SSE fails or is unsupported.
   */
  async watchJobSSE(
    jobId: string,
    callbacks: {
      onProgress?: (
        status: string,
        progress: number,
        message: string,
        meta?: {
          file_extraction_status?: Array<{
            file_name: string;
            file_index: number;
            status: string;
            error?: string;
          }>;
          pipeline_stage?: string;
        }
      ) => void;
      onPartialResult?: (htsClassification: any) => void;
      onReasoningEvent?: (event: { type: string; iteration: number; phase: string; data: any }) => void;
    }
  ): Promise<ProcessingResult> {
    const { getSessionToken } = await import('@descope/react-sdk');
    const token = await getSessionToken();
    const url = `${API_ENDPOINTS.BASE}${API_ENDPOINTS.DOCUMENTS.PROGRESS_SSE(jobId)}`;

    return new Promise<ProcessingResult>((resolve, reject) => {
      const controller = new AbortController();

      fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok || !response.body) {
            throw new Error(`SSE connection failed: ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let currentEventType = '';

          const processLine = (line: string) => {
            if (line.startsWith('event:')) {
              currentEventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              let data: any;
              try {
                data = JSON.parse(line.slice(5).trim());
              } catch {
                return; // ignore unparseable data lines
              }

              if (currentEventType === 'status' || currentEventType === 'update') {
                callbacks.onProgress?.(
                  data.status,
                  data.progress ?? 0,
                  data.message ?? '',
                  {
                    file_extraction_status: data.file_extraction_status,
                    pipeline_stage: data.pipeline_stage,
                  }
                );
                if (data.reasoning_event) {
                  callbacks.onReasoningEvent?.(data.reasoning_event);
                }
                if (data.partial_result?.hts_classification) {
                  console.log('[DocumentService] Partial HTS classification received:', data.partial_result.hts_classification);
                  callbacks.onPartialResult?.(data.partial_result.hts_classification);
                }
              } else if (currentEventType === 'complete') {
                controller.abort();
                if (data.final_status === 'completed') {
                  console.log('[DocumentService] Complete classification result received:', data.result);
                  console.log('[DocumentService] hts_classification:', data.result?.hts_classification);
                  resolve(data.result);
                } else {
                  reject(new Error(data.result?.error || 'Processing failed'));
                }
              } else if (currentEventType === 'error') {
                controller.abort();
                reject(new Error(data.message || 'SSE error from server'));
              }

              // Reset event type after processing data line
              currentEventType = '';
            }
          };

          // Read the SSE stream chunk by chunk
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            // Keep the last (potentially incomplete) line in the buffer
            buffer = lines.pop() ?? '';
            for (const line of lines) {
              processLine(line);
            }
          }
        })
        .catch((err: Error) => {
          if (err.name === 'AbortError') return; // we aborted — already resolved/rejected
          console.warn('[DocumentService] SSE failed, falling back to polling:', err.message);
          // Fallback: poll the REST endpoint
          this.pollJobStatus(jobId, (statusUpdate) => {
            callbacks.onProgress?.(statusUpdate.status, statusUpdate.progress ?? 0, statusUpdate.message ?? '');
            if (statusUpdate.partial_result?.hts_classification) {
              callbacks.onPartialResult?.(statusUpdate.partial_result.hts_classification);
            }
          })
            .then(resolve)
            .catch(reject);
        });
    });
  }

  /**
   * Error handler
   * @param error - Axios error object
   * @returns Formatted error with additional properties
   */
  handleError(error: AxiosError): DocumentServiceError {
    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;
      const message = (data as any)?.message || (data as any)?.error || 'An error occurred';
      
      const customError = new Error(message) as DocumentServiceError;
      customError.status = status;
      customError.data = data;
      
      return customError;
    } else if (error.request) {
      // Request made but no response
      const customError = new Error('No response from server. Please check your connection.') as DocumentServiceError;
      return customError;
    } else {
      // Something else happened
      return error as DocumentServiceError;
    }
  }
}

// Create and export singleton instance
const documentService = new DocumentService();

export { documentService };
export default documentService;
